import type { Config, DriverHook, DriveStep, Popover } from 'driver.js';
import { driver } from 'driver.js';
import type { ReactNode } from 'react';
import {
	createContext,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useRef,
	useState,
} from 'react';
import { useLocation, useNavigate } from 'react-router';
import 'driver.js/dist/driver.css';
import '../styles/tourStyles.css';
import { useGetTutorials } from '@/hooks/service-hooks/tutorials';
import { useAuthStore } from '@/store';
import type { Tutorial, TutorialStep } from '@/types/tutorials';
import {
	isOnboardingTutorial,
	mapTutorialToStepConfigs,
	type StepConfig,
	type TourElementKey,
	type TourElements,
	tutorialActions,
} from '@/utils/tutorials';

export type { TourElements };

interface ExtendedPopover extends Popover {
	subtitle?: string;
}

interface ExtendedDriveStep extends Omit<DriveStep, 'popover'> {
	popover?: ExtendedPopover;
}

const NAVIGATION_TIMEOUT = 1500;

interface TourContextType {
	registerElement: (key: TourElementKey, element: HTMLElement | null) => void;
	startTour: (tutorialId: string, startedManually?: boolean) => Promise<void>;
	stopTour: () => Promise<void>;
	isTourActive: boolean;
	getAvailableTutorials: () => Array<{
		id: string;
		name: string;
		enabled?: boolean;
	}>;
	activeTutorialId: string | null;
	activeTutorialName: string | null;
}

const TourContext = createContext<TourContextType | undefined>(undefined);

type TourEndReason = 'completed' | 'skipped';

interface TourProviderProps {
	children: ReactNode;
	autoStart?: boolean;
	onTourEnd?: (payload: {
		tutorialName: string;
		reason: TourEndReason;
		startedManually?: boolean;
	}) => void;
}

export const TourProvider = ({
	children,
	autoStart = false,
	onTourEnd,
}: TourProviderProps) => {
	const navigate = useNavigate();
	const location = useLocation();

	// Fetch tutorials from backend
	const { data: tutorials = [], isLoading: isLoadingTutorials } =
		useGetTutorials();
	const user = useAuthStore((state) => state.user);
	const permissions = user?.userInformation?.permissions;
	const userPermissions = useMemo(() => permissions ?? [], [permissions]);
	const userPort = user?.userInformation?.portsAvailable;

	const hasPermission = useCallback(
		(requiredPermissions?: string[]) => {
			if (!requiredPermissions || requiredPermissions.length === 0) {
				return true;
			}

			// Check if the user has all the required permissions
			return requiredPermissions.every((permission) =>
				userPermissions.includes(permission),
			);
		},
		[userPermissions],
	);

	const matchesPort = useCallback(
		(tutorialPort?: string) => {
			// If tutorial has no port specified, show it to all users
			if (!tutorialPort) {
				return true;
			}

			// If user has no port, don't show port-specific tutorials
			if (!userPort) {
				return false;
			}

			// Case-insensitive comparison
			return (
				tutorialPort.toLowerCase().trim() === userPort.toLowerCase().trim()
			);
		},
		[userPort],
	);

	const filterStepsByPermission = useCallback(
		(steps: StepConfig[]) =>
			steps.filter((step) => hasPermission(step.permissions)),
		[hasPermission],
	);

	const applyDynamicTitlesToSteps = useCallback((steps: StepConfig[]) => {
		return steps.map((step, index) => {
			const baseTitle =
				step.baseTitle || step.popover.title || `Step ${index + 1}`;
			return {
				...step,
				popover: {
					...step.popover,
					title: `Step ${index + 1}: ${baseTitle}`,
				},
			};
		});
	}, []);

	const locationRef = useRef(location.pathname);
	// Keeps track of every DOM node registered for the tour steps
	const elementsRef = useRef<
		Partial<Record<TourElementKey, HTMLElement | null>>
	>({});
	// Holds the active driver.js instance controlling the tour
	const driverRef = useRef<ReturnType<typeof driver> | null>(null);
	// Mirrors the currently configured steps with their resolved DOM nodes
	const activeStepsRef = useRef<ExtendedDriveStep[]>([]);
	// Prevents multiple route transitions from overlapping
	const isTransitioningRef = useRef(false);
	const pendingNavigationRef = useRef<{
		target: string;
		resolve: () => void;
	} | null>(null);
	// Stores callbacks waiting for an element to mount before a step runs
	const elementWaitersRef = useRef<
		Partial<Record<TourElementKey, Array<(element: HTMLElement) => void>>>
	>({});
	// Prevents tour from restarting immediately after completion
	const tourJustCompletedRef = useRef(false);
	// Tracks if the current tour was started manually
	const isManuallyStartedRef = useRef(false);

	const [isTourActive, setIsTourActive] = useState(false);
	const [activeTutorialId, setActiveTutorialId] = useState<string | null>(null);
	const [activeTutorialName, setActiveTutorialName] = useState<string | null>(
		null,
	);
	const activeTutorialNameRef = useRef<string | null>(null);
	const currentStepConfigsRef = useRef<StepConfig[]>([]);
	const dismissReasonRef = useRef<
		'overlay-click' | 'escaped' | 'cancelled' | null
	>(null);

	const resolveNavigationIfNeeded = useCallback(() => {
		// Resolve any pending navigation promise once the target route is active
		if (
			pendingNavigationRef.current &&
			locationRef.current === pendingNavigationRef.current.target
		) {
			pendingNavigationRef.current.resolve();
			pendingNavigationRef.current = null;
		}
	}, []);

	useEffect(() => {
		locationRef.current = location.pathname;
		resolveNavigationIfNeeded();
	}, [location.pathname, resolveNavigationIfNeeded]);

	// Handle overlay pointer down and key down events
	useEffect(() => {
		const handleOverlayPointerDown = (event: PointerEvent) => {
			if (
				!isTourActive ||
				driverRef.current?.getConfig().allowClose === false
			) {
				return;
			}

			const target = event.target;
			if (
				target instanceof Element &&
				(target.classList.contains('driver-overlay') ||
					target.closest('.driver-overlay'))
			) {
				dismissReasonRef.current = 'overlay-click';
			}
		};

		const handleKeyDown = (event: KeyboardEvent) => {
			if (
				!isTourActive ||
				driverRef.current?.getConfig().allowClose === false
			) {
				return;
			}
			if (event.key === 'Escape' || event.key === 'Esc') {
				dismissReasonRef.current = 'escaped';
			}
		};

		window.addEventListener('pointerdown', handleOverlayPointerDown, true);
		window.addEventListener('keydown', handleKeyDown, true);

		return () => {
			window.removeEventListener('pointerdown', handleOverlayPointerDown, true);
			window.removeEventListener('keydown', handleKeyDown, true);
		};
	}, [isTourActive]);

	// Wait for the element to be mounted before the step runs
	const waitForElement = useCallback(
		(
			key: TourElementKey,
			timeout = NAVIGATION_TIMEOUT,
		): Promise<HTMLElement> => {
			const existing = elementsRef.current[key];
			if (
				existing instanceof HTMLElement &&
				document.contains(existing)
			) {
				return Promise.resolve(existing);
			}
			if (existing && !document.contains(existing as Node)) {
				elementsRef.current[key] = null;
			}

			return new Promise<HTMLElement>((resolve, reject) => {
				const timerId = window.setTimeout(() => {
					cleanup();
					reject(new Error(`Timeout waiting for tour element "${key}"`));
				}, timeout);

				const cleanup = () => {
					window.clearTimeout(timerId);
					const waiters = elementWaitersRef.current[key];
					if (waiters) {
						elementWaitersRef.current[key] = waiters.filter(
							(fn) => fn !== resolver,
						);
					}
				};

				const resolver = (element: HTMLElement) => {
					cleanup();
					resolve(element);
				};

				elementWaitersRef.current[key] = [
					...(elementWaitersRef.current[key] ?? []),
					resolver,
				];
			});
		},
		[],
	);

	// Register the element for the tour step
	const registerElement = useCallback(
		(key: TourElementKey, element: HTMLElement | null) => {
			if (element) {
				elementsRef.current[key] = element;

				const waiters = elementWaitersRef.current[key];
				if (waiters?.length) {
					waiters.forEach((resolver) => {
						resolver(element);
					});
					delete elementWaitersRef.current[key];
				}

				const stepIndex = currentStepConfigsRef.current.findIndex(
					(step) => step.reference === key,
				);
				if (stepIndex >= 0 && activeStepsRef.current[stepIndex]) {
					activeStepsRef.current[stepIndex].element = element;
				}
			} else {
				elementsRef.current[key] = null;
			}
		},
		[],
	);

	// Ensure the route is loaded before the step runs
	const ensureRoute = useCallback(
		(route: string) => {
			if (route === locationRef.current) {
				return Promise.resolve();
			}

			return new Promise<void>((resolve) => {
				pendingNavigationRef.current = { target: route, resolve };
				navigate(route);
			});
		},
		[navigate],
	);

	// Ensure the route and element are loaded before the step runs
	const ensureRouteAndElement = useCallback(
		async (config: StepConfig) => {
			await ensureRoute(config.route);
			
			// Handle array references - use the first one for menu opening logic
			const references = Array.isArray(config.reference)
				? config.reference
				: [config.reference];
			const primaryReference = references[0] as TourElementKey;

			// Check if we're in mobile/tablet mode (max-width: 1030px)
			const isMobileTablet = window.innerWidth <= 1030;

			// In mobile/tablet mode, handle drawer open/close for topbar elements
			if (isMobileTablet) {
				const topbarRefs = [
					'InvoicesNavItemRef',
					'InvoicesSalesRepNavItemRef',
					'ReturnsNavItemRef',
					'OrdersNavItemRef',
					'ordersNavItemRef',
					'accountingFinanceNavItemRef',
					'invoicesNavItemRef',
					'BVBenefitVerificationNavItemRef',
				];
				
				// If this step requires a topbar element, open drawer
				if (topbarRefs.includes(primaryReference)) {
					try {
						// Determine which items to expand based on the reference
						const itemsToExpand: string[] = [];
						
						if (primaryReference === 'InvoicesNavItemRef' || primaryReference === 'InvoicesSalesRepNavItemRef') {
							// Check if user has Accounting & Finance permission
							const hasAccountingFinancePermission = hasPermission(['Accounting:Visualize']);
							
							// Use Accounting & Finance if user has permission (for both onboarding and individual tutorials)
							// Otherwise, use standalone Invoices menu
							if (hasAccountingFinancePermission) {
								itemsToExpand.push('Accounting & Finance');
								itemsToExpand.push('Accounting & Finance-Invoices');
							} else {
								itemsToExpand.push('Invoices');
							}
						} else if (primaryReference === 'ReturnsNavItemRef' || primaryReference === 'OrdersNavItemRef') {
							itemsToExpand.push('Orders');
						}
						
						// Dispatch event to open drawer
						const event = new CustomEvent('tour:openDrawer', {
							detail: { items: itemsToExpand },
						});
						window.dispatchEvent(event);
						
						// Also try the action (in case NavLinks is rendered)
						const rawStep: TutorialStep = {
							reference: primaryReference,
						} as TutorialStep;
						tutorialActions.run('openTobBarMenu', rawStep);
						
						// Wait for drawer to open and items to expand
						await new Promise((resolve) => setTimeout(resolve, 500));
					} catch (error) {
						console.error('[Tour] Error opening drawer for topbar element:', error);
					}
				} else {
					// If this step doesn't require a topbar element, close drawer
					try {
						window.dispatchEvent(new CustomEvent('tour:closeDrawer'));
					} catch (error) {
						console.error('[Tour] Error closing drawer:', error);
					}
				}
			}

			// Skip desktop menu opening logic if in mobile/tablet mode
			if (!isMobileTablet && primaryReference === 'InvoicesNavItemRef') {
				try {
					const rawStepForMenu: TutorialStep = {
						reference: primaryReference,
					} as TutorialStep;
					tutorialActions.run('openTobBarMenu', rawStepForMenu);
					await new Promise((resolve) => setTimeout(resolve, 150));
				} catch (error) {
					console.error('[Tour] Error opening topbar menu for Invoices step:', error);
				}

				const accountingFinanceElement = elementsRef.current.accountingFinanceNavItemRef;
				if (accountingFinanceElement instanceof HTMLElement) {
					try {
						const event = new MouseEvent('mouseover', { bubbles: true });
						accountingFinanceElement.dispatchEvent(event);
						await new Promise((resolve) => setTimeout(resolve, 300));
						
						let invoicesMenuItem: HTMLElement | null = null;
						let attempts = 0;
						const maxAttempts = 10;
						
						while (!invoicesMenuItem && attempts < maxAttempts) {
							await new Promise((resolve) => setTimeout(resolve, 100));
							
							const menuItems = document.querySelectorAll('[role="menuitem"]');
							for (const item of Array.from(menuItems)) {
								const text = item.textContent?.trim();
								const style = window.getComputedStyle(item);
								const rect = item.getBoundingClientRect();
								
								if (
									text === 'Invoices' &&
									style.display !== 'none' &&
									style.visibility !== 'hidden' &&
									rect.width > 0 &&
									rect.height > 0
								) {
									const hasChevron = item.querySelector('svg') !== null;
									if (hasChevron) {
										invoicesMenuItem = item as HTMLElement;
										break;
									}
								}
							}
							
							if (!invoicesMenuItem) {
								const allElements = document.querySelectorAll('*');
								for (const element of Array.from(allElements)) {
									const text = element.textContent?.trim();
									const style = window.getComputedStyle(element);
									const rect = element.getBoundingClientRect();
									
									if (
										text === 'Invoices' &&
										style.display !== 'none' &&
										style.visibility !== 'hidden' &&
										rect.width > 0 &&
										rect.height > 0
									) {
										const menuContainer = element.closest('[role="menu"]') || 
											element.closest('[data-state="open"]') ||
											element.closest('[data-chakra-menu-content]');
										if (menuContainer) {
											const parentMenuItem = element.closest('[role="menuitem"]') as HTMLElement;
											if (parentMenuItem) {
												const hasChevron = parentMenuItem.querySelector('svg') !== null;
												if (hasChevron) {
													invoicesMenuItem = parentMenuItem;
													break;
												}
											}
										}
									}
								}
							}
							
							attempts++;
						}
						
						if (invoicesMenuItem) {
							await new Promise((resolve) => requestAnimationFrame(() => resolve(undefined)));
							
							const mouseoverEvent = new MouseEvent('mouseover', {
								bubbles: true,
								cancelable: true,
								view: window,
							});
							invoicesMenuItem.dispatchEvent(mouseoverEvent);
							
							await new Promise((resolve) => setTimeout(resolve, 50));
							
							const mouseenterEvent = new MouseEvent('mouseenter', {
								bubbles: true,
								cancelable: true,
								view: window,
							});
							invoicesMenuItem.dispatchEvent(mouseenterEvent);
							
							const children = invoicesMenuItem.querySelectorAll('*');
							children.forEach((child) => {
								(child as HTMLElement).dispatchEvent(mouseenterEvent);
							});
							
							await new Promise((resolve) => setTimeout(resolve, 400));
						} else {
							console.warn('[Tour] Could not find "Invoices" menu item to open submenu after', maxAttempts, 'attempts.');
						}
					} catch (error) {
						console.error('[Tour] Failed to open Accounting & Finance menu and Invoices submenu:', error);
					}
				}
			} else if (!isMobileTablet && primaryReference === 'InvoicesSalesRepNavItemRef') {
				const invoicesElement = elementsRef.current.invoicesNavItemRef;
				if (invoicesElement instanceof HTMLElement) {
					try {
						const event = new MouseEvent('mouseover', { bubbles: true });
						invoicesElement.dispatchEvent(event);
						await new Promise((resolve) => setTimeout(resolve, 300));
						
						let foundElement = false;
						let attempts = 0;
						const maxAttempts = 10;
						
						while (!foundElement && attempts < maxAttempts) {
							await new Promise((resolve) => setTimeout(resolve, 100));
							
							if (elementsRef.current.InvoicesSalesRepNavItemRef instanceof HTMLElement) {
								foundElement = true;
								break;
							}
							
							const menuItems = document.querySelectorAll('[role="menuitem"]');
							for (const item of Array.from(menuItems)) {
								const text = item.textContent?.trim();
								const style = window.getComputedStyle(item);
								const rect = item.getBoundingClientRect();
								
								if (
									text === 'Open Invoices' &&
									style.display !== 'none' &&
									style.visibility !== 'hidden' &&
									rect.width > 0 &&
									rect.height > 0
								) {
									// Check if this is in the Invoices menu (not Accounting & Finance)
									const menuContainer = item.closest('[role="menu"]') || 
										item.closest('[data-state="open"]') ||
										item.closest('[data-chakra-menu-content]');
									if (menuContainer) {
										// Make sure we're not in the Accounting & Finance submenu
										const parentMenu = menuContainer.closest('[role="menu"]');
										if (!parentMenu || !parentMenu.textContent?.includes('Accounting & Finance')) {
											if (!elementsRef.current.InvoicesSalesRepNavItemRef) {
												registerElement('InvoicesSalesRepNavItemRef', item as HTMLElement);
												foundElement = true;
												break;
											}
										}
									}
								}
							}
							
							if (foundElement) break;
							attempts++;
						}
						
						if (!foundElement) {
							console.warn(`[Tour] Element ${primaryReference} (Open Invoices) not found after ${maxAttempts} attempts. Menu may not be fully rendered.`);
						}
						
						await new Promise((resolve) => setTimeout(resolve, 200));
					} catch (error) {
						console.error('[Tour] Failed to dispatch mouseover to open Invoices menu:', error);
					}
				}
			} else if (!isMobileTablet && (primaryReference === 'ReturnsNavItemRef' || primaryReference === 'OrdersNavItemRef')) {
				const ordersElement = elementsRef.current.ordersNavItemRef;
				if (ordersElement instanceof HTMLElement) {
					try {
						const event = new MouseEvent('mouseover', { bubbles: true });
						ordersElement.dispatchEvent(event);
						await new Promise((resolve) => setTimeout(resolve, 300));
						
						let foundElement = false;
						let attempts = 0;
						const maxAttempts = 10;
						
						const targetItemTitle = primaryReference === 'ReturnsNavItemRef' 
							? 'Return Orders' 
							: 'Order Status';
						
						while (!foundElement && attempts < maxAttempts) {
							await new Promise((resolve) => setTimeout(resolve, 100));
							
							if (primaryReference === 'ReturnsNavItemRef') {
								if (elementsRef.current.ReturnsNavItemRef instanceof HTMLElement) {
									foundElement = true;
									break;
								}
							} else if (primaryReference === 'OrdersNavItemRef') {
								if (elementsRef.current.OrdersNavItemRef instanceof HTMLElement) {
									foundElement = true;
									break;
								}
							}
							
							const menuItems = document.querySelectorAll('[role="menuitem"]');
							for (const item of Array.from(menuItems)) {
								const text = item.textContent?.trim();
								const style = window.getComputedStyle(item);
								const rect = item.getBoundingClientRect();
								
								if (
									text === targetItemTitle &&
									style.display !== 'none' &&
									style.visibility !== 'hidden' &&
									rect.width > 0 &&
									rect.height > 0
								) {
									if (primaryReference === 'ReturnsNavItemRef' && !elementsRef.current.ReturnsNavItemRef) {
										registerElement('ReturnsNavItemRef', item as HTMLElement);
										foundElement = true;
										break;
									} else if (primaryReference === 'OrdersNavItemRef' && !elementsRef.current.OrdersNavItemRef) {
										registerElement('OrdersNavItemRef', item as HTMLElement);
										foundElement = true;
										break;
									}
								}
							}
							
							if (foundElement) break;
							attempts++;
						}
						
						if (!foundElement) {
							console.warn(`[Tour] Element ${primaryReference} (${targetItemTitle}) not found after ${maxAttempts} attempts. Menu may not be fully rendered.`);
						}
						
						await new Promise((resolve) => setTimeout(resolve, 200));
					} catch (error) {
						console.error('[Tour] Failed to dispatch mouseover to open Orders menu:', error);
					}
				}
			}

			const element = await waitForElement(primaryReference);

			const stepIndex = currentStepConfigsRef.current.findIndex(
				(step) => {
					const stepRefs = Array.isArray(step.reference)
						? step.reference
						: [step.reference];
					return stepRefs.includes(primaryReference);
				},
			);
			if (stepIndex >= 0 && activeStepsRef.current[stepIndex]) {
				activeStepsRef.current[stepIndex].element = element;
			}
			return element;
		},
		[ensureRoute, waitForElement, registerElement, hasPermission],
	);

	// Stop the tour
	const stopTour = useCallback(async () => {
		// Fully dispose the current tour and reset the step cache
		if (driverRef.current) {
			try {
				driverRef.current.destroy();
			} catch (error) {
				console.error('[Tour] Error destroying driver in stopTour:', error);
			}
			driverRef.current = null;
			setIsTourActive(false);
			setActiveTutorialId(null);
			setActiveTutorialName(null);
			activeTutorialNameRef.current = null;
			dismissReasonRef.current = null;
			isTransitioningRef.current = false;
			currentStepConfigsRef.current = [];
			activeStepsRef.current = [];
		}
	}, []);

	// Get the active index of the current step
	const getActiveIndex = useCallback(() => {
		// Safely read the current step index across driver.js versions
		if (!driverRef.current) return 0;

		if (typeof driverRef.current.getActiveIndex === 'function') {
			return driverRef.current.getActiveIndex();
		}

		const state = driverRef.current.getState?.();
		return state?.activeIndex ?? 0;
	}, []);

	// Set the shared driver config
	const setSharedDriverConfig = useCallback(
		(driverInstance: ReturnType<typeof driver>) => {
			const options: Config = {
				animate: true,
				overlayOpacity: 0.5,
				stagePadding: 8,
				allowClose: false,
				showButtons: ['next', 'previous', 'close'],
				overlayColor: 'rgba(0,0,0,1)',
				doneBtnText: 'Finish →',
				nextBtnText: 'Next →',
			};

			driverInstance.setConfig({
				...options,
				// Prepare next step before driver.js advances, ensuring the DOM and route are ready
				onNextClick: (_element, _step, opts) => {
					if (isTransitioningRef.current) {
						return;
					}

					const currentIndex = getActiveIndex();
					const steps = currentStepConfigsRef.current;

					// Try to trigger backend action (onNextAction) for the current step
					try {
						const currentStepConfig = steps[currentIndex];
						let actionName = currentStepConfig?.onNextAction;
						let rawStep: TutorialStep | undefined;
						
						if (!actionName) {
							const activeTutorialName = activeTutorialNameRef.current;
							const originalTutorial: Tutorial | undefined = tutorials.find(
								(t) => t.name === activeTutorialName,
							);
							rawStep = originalTutorial?.steps?.[currentIndex];
							actionName = (rawStep as any)?.onNextAction as string | undefined;
						} else {
								rawStep = {
								reference: currentStepConfig.reference,
								route: currentStepConfig.route,
								title: currentStepConfig.baseTitle,
								description: currentStepConfig.popover.description || '',
							} as TutorialStep;
						}
						
						if (actionName && actionName.trim() !== '') {
							tutorialActions.run(actionName, rawStep);
						}
					} catch (error) {
						console.error(
							'[Tour] Error executing tutorial onNextAction:',
							error,
						);
					}

					const nextConfig = steps[currentIndex + 1];
					const isMobileTablet = window.innerWidth <= 1030;
					const topbarRefs = [
						'InvoicesNavItemRef',
						'InvoicesSalesRepNavItemRef',
						'ReturnsNavItemRef',
						'OrdersNavItemRef',
						'ordersNavItemRef',
						'accountingFinanceNavItemRef',
						'invoicesNavItemRef',
						'BVBenefitVerificationNavItemRef',
					];

					// Check if current step requires topbar (to close drawer if next doesn't)
					const currentConfig = steps[currentIndex];
					const currentRequiresTopbar = currentConfig?.reference && 
						(Array.isArray(currentConfig.reference)
							? currentConfig.reference.some(ref => topbarRefs.includes(ref as string))
							: topbarRefs.includes(currentConfig.reference as string));

					// Check if next step requires topbar
					const nextRequiresTopbar = nextConfig?.reference && 
						(Array.isArray(nextConfig.reference)
							? nextConfig.reference.some(ref => topbarRefs.includes(ref as string))
							: topbarRefs.includes(nextConfig.reference as string));

					// Close drawer if current step needed topbar but next doesn't (mobile/tablet only)
					if (isMobileTablet && currentRequiresTopbar && !nextRequiresTopbar) {
						try {
							window.dispatchEvent(new CustomEvent('tour:closeDrawer'));
						} catch (error) {
							console.error('[Tour] Error closing drawer on step transition:', error);
						}
					}

					// Automatically open topbar menu if next step requires a topbar submenu item
					// This handles onboarding tutorials where steps reference submenu items
					if (nextConfig?.reference) {
						const invoicesSubmenuRefs = [
							'InvoicesNavItemRef',
							'InvoicesSalesRepNavItemRef',
						];
						const ordersSubmenuRefs = [
							'ReturnsNavItemRef',
							'OrdersNavItemRef',
						];

						// Check if the next step references a topbar submenu item
						if (
							invoicesSubmenuRefs.includes(nextConfig.reference as string) ||
							ordersSubmenuRefs.includes(nextConfig.reference as string)
						) {
							try {
								// Pass the nextConfig as step so openTobBarMenu can determine which menu to open
								const rawStep: TutorialStep = {
									reference: nextConfig.reference,
								} as TutorialStep;
								tutorialActions.run('openTobBarMenu', rawStep);
							} catch (error) {
								console.error(
									'[Tour] Error auto-opening topbar menu:',
									error,
								);
							}
						}
					}

					if (!nextConfig) {
						// Tour completed - close drawer if open (mobile/tablet only)
						const isMobileTablet = window.innerWidth <= 1030;
						if (isMobileTablet) {
							try {
								window.dispatchEvent(new CustomEvent('tour:closeDrawer'));
							} catch (error) {
								console.error('[Tour] Error closing drawer on tour completion:', error);
							}
						}

						// Tour completed - prevent any further actions
						const completedTutorialName = activeTutorialNameRef.current;

						// Mark tour as just completed to prevent immediate restart
						tourJustCompletedRef.current = true;

						// Clear state IMMEDIATELY to prevent onDestroyed from interfering
						// Do this BEFORE destroying the driver so onDestroyed sees cleared state
						activeTutorialNameRef.current = null;
						setIsTourActive(false);
						setActiveTutorialId(null);
						setActiveTutorialName(null);

						// Clear step refs before destroying
						currentStepConfigsRef.current = [];
						activeStepsRef.current = [];
						isTransitioningRef.current = false;

						// Store driver reference before clearing
						const driverToDestroy = driverRef.current;
						driverRef.current = null;

						// Destroy the driver instance AFTER clearing refs to prevent callbacks from seeing active state
						if (driverToDestroy) {
							try {
								// Use setTimeout to ensure state is fully cleared before driver cleanup
								setTimeout(() => {
									try {
										driverToDestroy.destroy();
									} catch {
										// Driver may already be destroyed, ignore errors
									}
								}, 0);
							} catch (error) {
								console.error(
									'[Tour] Error preparing driver destruction:',
									error,
								);
							}
						}

						// Call onTourEnd callback when tour completes
						// For onboarding tutorials or any tutorial started manually
						const wasManuallyStarted = isManuallyStartedRef.current;
						if (
							completedTutorialName &&
							(isOnboardingTutorial(completedTutorialName) || wasManuallyStarted)
						) {
							onTourEnd?.({
								tutorialName: completedTutorialName,
								reason: 'completed',
								startedManually: wasManuallyStarted,
							});
						}

						// Reset the manual flag after calling onTourEnd
						isManuallyStartedRef.current = false;

						// Reset the flag after a delay to allow store updates to complete
						setTimeout(() => {
							tourJustCompletedRef.current = false;
						}, 2000);

						return;
					}

					isTransitioningRef.current = true;
					let prepareSucceeded = false;
					ensureRouteAndElement(nextConfig)
						.then(() => {
							prepareSucceeded = true;
						})
						.catch((error) => {
							console.error('[Tour] Failed to prepare next step', error);
						})
						.finally(() => {
							isTransitioningRef.current = false;
							if (!prepareSucceeded) {
								return;
							}
							try {
								if (typeof opts.driver.refresh === 'function') {
									opts.driver.refresh();
								}
								opts.driver.moveNext();
							} catch (err) {
								console.error('[Tour] Error moving to next step', err);
							}
						});
				},
				// Mirror the same behavior when stepping backward, including skip logic on the first step
				onPrevClick: (_element, _step, opts) => {
					if (opts.driver.isFirstStep()) {
						const skippedTutorialName = activeTutorialNameRef.current;

						// Mark tour as just completed to prevent immediate restart
						tourJustCompletedRef.current = true;

						// Clear state IMMEDIATELY before destroying (same pattern as completion)
						activeTutorialNameRef.current = null;
						setIsTourActive(false);
						setActiveTutorialId(null);
						setActiveTutorialName(null);

						// Clear step refs before destroying
						currentStepConfigsRef.current = [];
						activeStepsRef.current = [];
						isTransitioningRef.current = false;

						// Store driver reference before clearing
						const driverToDestroy = opts.driver;
						driverRef.current = null;

						// Destroy the driver instance AFTER clearing refs (async, like completion)
						setTimeout(() => {
							try {
								driverToDestroy.destroy();
							} catch (error) {
								console.error('[Tour] Error destroying driver on skip:', error);
							}
						}, 0);

						// Call onTourEnd callback when tour is skipped on first step
						// For onboarding tutorials or any tutorial started manually
						const wasManuallyStarted = isManuallyStartedRef.current;
						if (
							skippedTutorialName &&
							(isOnboardingTutorial(skippedTutorialName) || wasManuallyStarted)
						) {
							onTourEnd?.({
								tutorialName: skippedTutorialName,
								reason: 'skipped',
								startedManually: wasManuallyStarted,
							});
						}

						// Reset the manual flag after calling onTourEnd
						isManuallyStartedRef.current = false;

						// Reset the flag after a delay to allow store updates to complete
						setTimeout(() => {
							tourJustCompletedRef.current = false;
						}, 2000);

						return;
					}

					if (isTransitioningRef.current) {
						return;
					}

					const currentIndex = getActiveIndex();
					const steps = currentStepConfigsRef.current;

					const previousIndex = Math.max(currentIndex - 1, 0);
					const previousConfig = steps[previousIndex];

					if (!previousConfig) {
						opts.driver.movePrevious();
						return;
					}

					// Try to trigger backend action (onPrevAction) for the current step
					// This action may change the DOM (close modals, change form steps, etc.)
					let hasAction = false;
					try {
						const currentStepConfig = steps[currentIndex];
						let actionName = currentStepConfig?.onPrevAction;
						let rawStep: TutorialStep | undefined;
						
						if (!actionName) {
							const activeTutorialName = activeTutorialNameRef.current;
							const originalTutorial: Tutorial | undefined = tutorials.find(
								(t) => t.name === activeTutorialName,
							);
							rawStep = originalTutorial?.steps?.[currentIndex];
							actionName = (rawStep as any)?.onPrevAction as string | undefined;
						} else {
							rawStep = {
								reference: currentStepConfig.reference,
								route: currentStepConfig.route,
								title: currentStepConfig.baseTitle,
								description: currentStepConfig.popover.description || '',
							} as TutorialStep;
						}
						
						if (actionName && actionName.trim() !== '') {
							tutorialActions.run(actionName, rawStep);
							hasAction = true;
						}
					} catch (error) {
						console.error(
							'[Tour] Error executing tutorial onPrevAction:',
							error,
						);
					}

					// Prepare the previous step element and move to it
					// This ensures the element is ready and highlighted correctly
					const prepareAndMove = () => {
						if (isTransitioningRef.current) {
							return;
						}
						isTransitioningRef.current = true;
						ensureRouteAndElement(previousConfig)
							.catch((error) => {
								console.error('[Tour] Failed to prepare previous step', error);
							})
							.finally(() => {
								if (typeof opts.driver.refresh === 'function') {
									opts.driver.refresh();
								}
								opts.driver.movePrevious();
								isTransitioningRef.current = false;
							});
					};

					// If there was an action, wait for DOM to render before finding the element
					// Actions may change the DOM (close modals, change form steps, etc.)
					if (hasAction) {
						// Use requestAnimationFrame to wait for the next paint, then setTimeout
						// to ensure DOM updates from actions have completed
						requestAnimationFrame(() => {
							setTimeout(() => {
								prepareAndMove();
							}, 300);
						});
						return;
					}

					// No action - prepare element immediately
					prepareAndMove();
				},
				// Normalize the popover layout across breakpoints and inject subtitles
				onPopoverRender: (popover, opts) => {
					const popoverElement = popover.wrapper;
					if (popoverElement) {
						const viewportWidth = window.innerWidth;
						const viewportHeight = window.innerHeight;
						const isShortViewport = viewportHeight <= 800; // e.g. 1366×768 @ 13"
						const isMobile = viewportWidth <= 480;
						const isSmallTablet = viewportWidth > 480 && viewportWidth <= 767;
						const isIpadMini = viewportWidth >= 768 && viewportWidth <= 833;
						const isIpadPro11 = viewportWidth >= 834 && viewportWidth <= 1023;
						const isIpadPro129 = viewportWidth >= 1024 && viewportWidth <= 1366;

						popoverElement.style.position = 'fixed';
						popoverElement.style.display = 'flex';
						popoverElement.style.flexDirection = 'column';
						popoverElement.style.transform = 'none';

						// Use rem/vw for DPI-independent scaling across different displays
						// Short viewport (e.g. 1366×768): smaller popover so it doesn't dominate the screen
						if (isShortViewport && !isMobile) {
							popoverElement.style.width = 'min(26rem, 32vw)';
							popoverElement.style.maxWidth = 'min(26rem, 32vw)';
							popoverElement.style.height = 'auto';
							popoverElement.style.minHeight = 'min(12rem, 45vh)';
							popoverElement.style.maxHeight = 'calc(100vh - 2rem)';
							popoverElement.style.bottom = 'min(1rem, 2vh)';
							popoverElement.style.right = 'min(1rem, 2vw)';
							popoverElement.style.left = 'auto';
							popoverElement.style.padding = '0.75rem';
						} else if (isMobile) {
							popoverElement.style.width = 'calc(100vw - 1.5rem)';
							popoverElement.style.maxWidth = 'none';
							popoverElement.style.height = 'auto';
							popoverElement.style.minHeight = 'min(11.25rem, 30vh)';
							popoverElement.style.maxHeight = 'calc(100vh - 2.5rem)';
							popoverElement.style.bottom = 'min(0.75rem, 1.5vh)';
							popoverElement.style.right = 'min(0.75rem, 2vw)';
							popoverElement.style.left = 'min(0.75rem, 2vw)';
						} else if (isSmallTablet) {
							popoverElement.style.width = 'min(26.25rem, 55vw)';
							popoverElement.style.maxWidth = 'min(26.25rem, 55vw)';
							popoverElement.style.height = 'auto';
							popoverElement.style.minHeight = 'min(11.875rem, 32vh)';
							popoverElement.style.maxHeight = 'calc(100vh - 2.75rem)';
							popoverElement.style.bottom = 'min(0.875rem, 1.5vh)';
							popoverElement.style.right = 'min(1rem, 2vw)';
							popoverElement.style.left = 'auto';
						} else if (isIpadMini) {
							popoverElement.style.width = 'min(28.125rem, 45vw)';
							popoverElement.style.maxWidth = 'min(28.125rem, 45vw)';
							popoverElement.style.height = 'auto';
							popoverElement.style.minHeight = 'min(12.5rem, 35vh)';
							popoverElement.style.maxHeight = 'calc(100vh - 3rem)';
							popoverElement.style.bottom = 'min(1rem, 2vh)';
							popoverElement.style.right = 'min(1rem, 2vw)';
							popoverElement.style.left = 'auto';
						} else if (isIpadPro11) {
							popoverElement.style.width = 'min(30rem, 42vw)';
							popoverElement.style.maxWidth = 'min(30rem, 42vw)';
							popoverElement.style.height = 'auto';
							popoverElement.style.minHeight = 'min(13.125rem, 36vh)';
							popoverElement.style.maxHeight = 'calc(100vh - 2.75rem)';
							popoverElement.style.bottom = 'min(1.125rem, 2vh)';
							popoverElement.style.right = 'min(1.25rem, 2vw)';
							popoverElement.style.left = 'auto';
						} else if (isIpadPro129) {
							popoverElement.style.width = 'min(32.5rem, 40vw)';
							popoverElement.style.maxWidth = 'min(32.5rem, 40vw)';
							popoverElement.style.height = 'auto';
							popoverElement.style.minHeight = 'min(13.75rem, 38vh)';
							popoverElement.style.maxHeight = 'calc(100vh - 3rem)';
							popoverElement.style.bottom = 'min(1.25rem, 2.5vh)';
							popoverElement.style.right = 'min(1.5rem, 2vw)';
							popoverElement.style.left = 'auto';
						} else {
							popoverElement.style.width = 'min(31.25rem, 38vw)';
							popoverElement.style.maxWidth = 'min(31.25rem, 38vw)';
							popoverElement.style.height = 'auto';
							popoverElement.style.minHeight = 'min(15.125rem, 35vh)';
							popoverElement.style.maxHeight = 'calc(100vh - 3rem)';
							popoverElement.style.bottom = 'min(1.5rem, 3vh)';
							popoverElement.style.right = 'min(1.5rem, 3vw)';
							popoverElement.style.left = 'auto';
						}
					}

					if (popover.footer) {
						popover.footer.style.marginTop = 'auto';
						popover.footer.style.flexShrink = '0';
					}

					const titleElement = popover.title;
					const parent = titleElement?.parentElement;
					if (parent) {
						parent.style.flex = '1';
						parent.style.display = 'flex';
						parent.style.flexDirection = 'column';
						parent.style.overflowY = 'auto';
						parent.style.minHeight = '0';
					}

					const isFirstStep = opts.driver.isFirstStep();
					const previousButton = popover.previousButton as
						| (HTMLButtonElement & {
								__tourSkipHandler?: (event: MouseEvent) => void;
						  })
						| null;
					const closeButton = popover.closeButton as
						| (HTMLButtonElement & {
								__tourSkipHandler?: (event: MouseEvent) => void;
						  })
						| null;

					const handleSkip = (
						event?: MouseEvent,
						options?: { allowDriverDefault?: boolean },
					) => {
						if (!options?.allowDriverDefault) {
							event?.preventDefault();
							event?.stopPropagation();
						}

						const skippedTutorialName = activeTutorialNameRef.current;

						// Mark tour as just completed to prevent immediate restart
						tourJustCompletedRef.current = true;

						activeTutorialNameRef.current = null;
						setIsTourActive(false);
						setActiveTutorialId(null);
						setActiveTutorialName(null);

						// Clear step refs before destroying
						currentStepConfigsRef.current = [];
						activeStepsRef.current = [];
						isTransitioningRef.current = false;

						// Store driver reference before clearing
						const driverToDestroy = opts.driver;
						driverRef.current = null;

						// Destroy the driver instance AFTER clearing refs (async, like completion)
						setTimeout(() => {
							try {
								driverToDestroy.destroy();
							} catch (error) {
								console.error('[Tour] Error destroying driver on skip:', error);
							}
						}, 0);

						// Call onTourEnd callback when tour is skipped
						// For onboarding tutorials or any tutorial started manually
						const wasManuallyStarted = isManuallyStartedRef.current;
						if (
							skippedTutorialName &&
							(isOnboardingTutorial(skippedTutorialName) || wasManuallyStarted)
						) {
							onTourEnd?.({
								tutorialName: skippedTutorialName,
								reason: 'skipped',
								startedManually: wasManuallyStarted,
							});
						}

						// Reset the manual flag after calling onTourEnd
						isManuallyStartedRef.current = false;

						// Reset the flag after a delay to allow store updates to complete
						setTimeout(() => {
							tourJustCompletedRef.current = false;
						}, 2000);
					};

					if (isFirstStep && previousButton) {
						if (previousButton.__tourSkipHandler) {
							previousButton.removeEventListener(
								'click',
								previousButton.__tourSkipHandler,
								true,
							);
						}

						previousButton.removeAttribute('disabled');
						previousButton.textContent = 'Skip';
						previousButton.className =
							'driver-popover-prev-btn driver-popover-skip-btn';

						const skipHandler = (event: MouseEvent) => {
							handleSkip(event);
						};

						previousButton.__tourSkipHandler = skipHandler;
						previousButton.addEventListener('click', skipHandler, true);
					} else if (previousButton) {
						if (previousButton.__tourSkipHandler) {
							previousButton.removeEventListener(
								'click',
								previousButton.__tourSkipHandler,
								true,
							);
							delete previousButton.__tourSkipHandler;
						}
						previousButton.textContent = opts.config.prevBtnText || 'Previous';
						previousButton.className = 'driver-popover-prev-btn';
						previousButton.removeAttribute('disabled');
					}

					if (!isFirstStep && closeButton) {
						if (closeButton.__tourSkipHandler) {
							closeButton.removeEventListener(
								'click',
								closeButton.__tourSkipHandler,
								true,
							);
						}
						closeButton.style.display = 'flex';
						closeButton.textContent = '';
						closeButton.classList.add('driver-popover-inline-skip-btn');
						closeButton.removeAttribute('disabled');
						const skipHandler = (event: MouseEvent) => {
							handleSkip(event, { allowDriverDefault: false });
						};
						closeButton.__tourSkipHandler = skipHandler;
						closeButton.addEventListener('click', skipHandler, true);
					} else if (closeButton) {
						if (closeButton.__tourSkipHandler) {
							closeButton.removeEventListener(
								'click',
								closeButton.__tourSkipHandler,
								true,
							);
							delete closeButton.__tourSkipHandler;
						}
						closeButton.classList.remove('driver-popover-inline-skip-btn');
						closeButton.style.display = 'none';
					}

					const isLastStep = opts.driver.isLastStep();
					if (isLastStep && popover.nextButton) {
						const viewportWidth = window.innerWidth;
						const isMobile = viewportWidth <= 480;
						const isSmallTablet = viewportWidth > 480 && viewportWidth <= 767;
						const isIpadMini = viewportWidth >= 768 && viewportWidth <= 833;

						// rem for DPI-independent button sizing
						if (isMobile) {
							popover.nextButton.style.width = '100%';
							popover.nextButton.style.height = '2.25rem';
							popover.nextButton.style.padding = '0.375rem 0.75rem';
						} else if (isSmallTablet || isIpadMini) {
							popover.nextButton.style.width = '100%';
							popover.nextButton.style.height = '2.375rem';
							popover.nextButton.style.padding = '0.4375rem 0.875rem';
						} else {
							popover.nextButton.style.width = '12.5625rem';
							popover.nextButton.style.height = '2.5rem';
							popover.nextButton.style.padding = '0.5rem 0.625rem';
						}
						popover.nextButton.style.background =
							'linear-gradient(to right, #117964, #1c4b74)';
						popover.nextButton.className =
							'driver-popover-next-btn driver-popover-done-btn';
					}

					const currentStep = opts.state.activeStep;
					if (currentStep?.popover) {
						const extendedPopover = currentStep.popover as ExtendedPopover;
						const subtitle = extendedPopover.subtitle;
						if (subtitle) {
							const existingSubtitle =
								popover.title.parentElement?.querySelector(
									'.driver-popover-subtitle',
								);
							if (existingSubtitle) {
								existingSubtitle.remove();
							}
							const subtitleElement = document.createElement('div');
							subtitleElement.className = 'driver-popover-subtitle';
							subtitleElement.textContent = subtitle;
							popover.title.parentElement?.insertBefore(
								subtitleElement,
								popover.title.nextSibling,
							);
						}
					}

					// Scroll to top when DataTable is highlighted (check via active step element)
					const activeElement = currentStep?.element;
					if (
						activeElement &&
						((activeElement instanceof Element &&
							activeElement.getAttribute('data-id') === 'smart-data-table') ||
							(activeElement instanceof Element &&
								activeElement.closest('[data-id="smart-data-table"]')))
					) {
						// Scroll to top of the page
						window.scrollTo({ top: 0, behavior: 'smooth' });
					}
				},
				// Scroll to top when DataTable is highlighted, or scroll horizontal for checkbox columns
				onHighlightStarted: (element) => {
					// Check if the highlighted element is the DataTable (has data-id="smart-data-table")
					if (
						element &&
						(element.getAttribute('data-id') === 'smart-data-table' ||
							element.closest('[data-id="smart-data-table"]'))
					) {
						// Scroll to top of the page
						window.scrollTo({ top: 0, behavior: 'smooth' });
					}
					
					// Check if the highlighted element is the checkbox column wrapper for invoices
					if (
						element &&
						element instanceof HTMLElement &&
						element.getAttribute('data-tour-column-wrapper') === 'true' &&
						element.getAttribute('data-column-index') === '0'
					) {
						// Find the table container with horizontal scroll
						const tableContainer = element.ownerDocument.querySelector('[data-id="smart-data-table"]');
						if (tableContainer && tableContainer instanceof HTMLElement) {
							// Find the scrollable container (Table.ScrollArea from Chakra UI)
							// It has class "chakra-table__scroll-area" and has overflow-x: auto
							const scrollableContainer = tableContainer.querySelector('.chakra-table__scroll-area') ||
								tableContainer.querySelector('[data-scroll-area]') ||
								Array.from(tableContainer.querySelectorAll('*')).find((el) => {
									if (!(el instanceof HTMLElement)) return false;
									const style = window.getComputedStyle(el);
									return style.overflowX === 'auto' || style.overflowX === 'scroll';
								});
							
							if (scrollableContainer && scrollableContainer instanceof HTMLElement) {
								// Get the first checkbox cell to calculate scroll position
								const table = scrollableContainer.querySelector('table');
								if (table) {
									const firstDataRow = table.querySelector('tbody tr:first-child');
									if (firstDataRow) {
										const firstCell = firstDataRow.children[0] as HTMLElement;
										if (firstCell) {
											// Function to update wrapper position manually
											const updateWrapperPosition = () => {
												const cellRect = firstCell.getBoundingClientRect();
												if (cellRect.width > 0 && cellRect.height > 0) {
													// Find all rows to calculate total height
													const allRows = table.querySelectorAll('tbody tr');
													if (allRows.length > 0) {
														const lastRow = allRows[allRows.length - 1];
														const lastCell = lastRow.children[0] as HTMLElement;
														if (lastCell) {
															const lastCellRect = lastCell.getBoundingClientRect();
															const totalHeight = lastCellRect.bottom - cellRect.top;
															
															// Update wrapper position
															element.style.left = `${cellRect.left}px`;
															element.style.top = `${cellRect.top}px`;
															element.style.width = `${cellRect.width}px`;
															element.style.height = `${totalHeight}px`;
															element.style.maxHeight = `${totalHeight}px`;
														}
													}
												}
											};
											
											// First, do immediate scroll (no smooth) to get into position quickly
											const containerRect = scrollableContainer.getBoundingClientRect();
											const cellRect = firstCell.getBoundingClientRect();
											
											// Check if checkbox column is outside the visible area
											if (cellRect.left < containerRect.left || cellRect.right > containerRect.right) {
												// Calculate the scroll position needed to show the checkbox column
												const scrollLeft = scrollableContainer.scrollLeft + (cellRect.left - containerRect.left) - 10; // 10px padding
												scrollableContainer.scrollLeft = Math.max(0, scrollLeft);
												
												// Update wrapper position immediately after scroll
												requestAnimationFrame(() => {
													updateWrapperPosition();
													// Also update again after a small delay to ensure it's correct
													setTimeout(() => {
														updateWrapperPosition();
													}, 50);
												});
											} else {
												// Even if already visible, update wrapper position to ensure it's correct
												updateWrapperPosition();
											}
										}
									}
								}
							}
						}
					}

					// Check if the highlighted element is the "Add Wound" button
					if (
						element &&
						element instanceof HTMLElement &&
						(element.textContent?.includes('Add Wound') || 
						 element.querySelector('button')?.textContent?.includes('Add Wound'))
					) {
						// Find the scrollable container parent (woundDetailsRef)
						let scrollableContainer: HTMLElement | null = null;
						let current: HTMLElement | null = element;
						
						while (current && current !== document.body) {
							const style = window.getComputedStyle(current);
							if (style.overflow === 'auto' || style.overflow === 'scroll' || 
								style.overflowY === 'auto' || style.overflowY === 'scroll') {
								scrollableContainer = current;
								break;
							}
							current = current.parentElement;
						}
						
						if (scrollableContainer) {
							const buttonRect = element.getBoundingClientRect();
							const containerRect = scrollableContainer.getBoundingClientRect();
							
							// Check if button is outside visible area
							const isAbove = buttonRect.top < containerRect.top;
							const isBelow = buttonRect.bottom > containerRect.bottom;
							
							if (isAbove || isBelow) {
								// Calculate scroll position to center the button in the container
								const scrollTop = scrollableContainer.scrollTop + 
									(buttonRect.top - containerRect.top) - 
									(containerRect.height / 2) + 
									(buttonRect.height / 2);
								
								scrollableContainer.scrollTo({
									top: Math.max(0, scrollTop),
									behavior: 'smooth'
								});
							}
						}
					}
				},
				// Detect skip for overlay click or ESCAPE (tracked manually)
				onDeselected: (..._args: Parameters<DriverHook>) => {
					void _args;
					const reason = dismissReasonRef.current;
					const tourStillActive = isTourActive && driverRef.current !== null;
					// Only handle skip if reason is set (user action) AND tour is still active
					// onDeselected is called normally when moving between steps (reason = null)
					// Also ignore if tour was already cleaned up (driverRef is null)
					if (
						reason !== null &&
						reason !== undefined &&
						(reason === 'overlay-click' ||
							reason === 'escaped' ||
							reason === 'cancelled') &&
						tourStillActive
					) {
						console.log('[Tour] onDeselected: handling skip (overlay/esc)');
						// Clear reason immediately to prevent multiple calls
						dismissReasonRef.current = null;
						const skippedName = activeTutorialNameRef.current;

						// Mark tour as just completed to prevent immediate restart
						tourJustCompletedRef.current = true;

						// Clear state IMMEDIATELY before destroying (same pattern as completion)
						activeTutorialNameRef.current = null;
						setIsTourActive(false);
						setActiveTutorialId(null);
						setActiveTutorialName(null);

						// Clear step refs before destroying
						currentStepConfigsRef.current = [];
						activeStepsRef.current = [];
						isTransitioningRef.current = false;

						// Store driver reference before clearing
						const driverToDestroy = driverInstance;
						driverRef.current = null;

						// Destroy the driver instance AFTER clearing refs (async, like completion)
						setTimeout(() => {
							try {
								driverToDestroy.destroy();
							} catch (error) {
								console.error(
									'[Tour] Error destroying driver on deselect:',
									error,
								);
							}
						}, 0);

						const wasManuallyStarted = isManuallyStartedRef.current;
						if (
							skippedName &&
							(isOnboardingTutorial(skippedName) || wasManuallyStarted)
						) {
							onTourEnd?.({
								tutorialName: skippedName,
								reason: 'skipped',
								startedManually: wasManuallyStarted,
							});
						}

						// Reset the manual flag after calling onTourEnd
						isManuallyStartedRef.current = false;

						// Reset the flag after a delay to allow store updates to complete
						setTimeout(() => {
							tourJustCompletedRef.current = false;
						}, 2000);
					}
				},

				// Detect any unexpected tour destruction
				onDestroyed: () => {
					// Only handle unexpected destruction if tour was still active
					// Skip if tour was already cleaned up (e.g., after completion)
					const skippedName = activeTutorialNameRef.current;

					// If driver instance was already destroyed and state cleared, do nothing
					// This prevents onDestroyed from running after a proper completion
					if (!driverRef.current && !skippedName) {
						return;
					}

					// Use driverRef to check if tour is still active instead of isTourActive state
					const isStillActive =
						driverRef.current !== null && skippedName !== null;

					// If tour was already properly cleaned up, just clear driverRef if it exists
					if (!skippedName || !isStillActive) {
						if (driverRef.current) {
							try {
								driverRef.current.destroy();
							} catch {
								// Ignore errors from already destroyed driver
							}
							driverRef.current = null;
						}
						return;
					}

					// This is an unexpected destruction (not a normal completion)
					setIsTourActive(false);
					setActiveTutorialId(null);
					setActiveTutorialName(null);
					activeTutorialNameRef.current = null;

					if (driverRef.current) {
						try {
							driverRef.current.destroy();
						} catch {
							// Ignore errors
						}
						driverRef.current = null;
					}

					// Clear step refs
					currentStepConfigsRef.current = [];
					activeStepsRef.current = [];
					isTransitioningRef.current = false;

					const wasManuallyStarted = isManuallyStartedRef.current;
					if (
						skippedName &&
						(isOnboardingTutorial(skippedName) || wasManuallyStarted)
					) {
						onTourEnd?.({
							tutorialName: skippedName,
							reason: 'skipped',
							startedManually: wasManuallyStarted,
						});
					}

					// Reset the manual flag after calling onTourEnd
					isManuallyStartedRef.current = false;
				},
			});

			return options;
		},
		[
			ensureRouteAndElement,
			getActiveIndex,
			onTourEnd,
			setActiveTutorialId,
			setIsTourActive,
			tutorials,
		],
	);

	// Start the tour
	const startTour = useCallback(
		async (tutorialName: string, startedManually = false) => {
			// Prevent tour from starting if it just completed
			if (tourJustCompletedRef.current) {
				console.warn('[Tour] Tour just completed, preventing restart');
				return;
			}

			if (isLoadingTutorials) {
				console.warn('[Tour] Tutorials are still loading');
				return;
			}

			// Find tutorial by name
			const tutorial = tutorials.find((t) => t.name === tutorialName);
			if (!tutorial) {
				console.warn(
					`[Tour] Tutorial with name "${tutorialName}" not found. Available tutorials:`,
					tutorials.map((t) => t.name),
				);
				return;
			}

			// Check if tutorial is enabled
			if (tutorial.enabled === false) {
				console.warn(
					`[Tour] Tutorial "${tutorialName}" is disabled and cannot be started.`,
				);
				return;
			}

			if (!hasPermission(tutorial.permissions)) {
				console.warn(
					`[Tour] User does not have the required permissions for "${tutorialName}".`,
				);
				return;
			}

			// Check if tutorial matches user's port
			const tutorialPort = tutorial.portsAvailable as string | undefined;
			if (!matchesPort(tutorialPort)) {
				console.warn(
					`[Tour] Tutorial "${tutorialName}" is not available for the current portal.`,
				);
				return;
			}

			const steps = mapTutorialToStepConfigs(tutorial);
			if (!steps || steps.length === 0) {
				console.warn(`[Tour] Tutorial "${tutorialName}" has no steps`);
				return;
			}

			const permittedSteps = filterStepsByPermission(steps);
			if (permittedSteps.length === 0) {
				console.warn(
					`[Tour] Tutorial "${tutorialName}" has no available steps for the current user`,
				);
				return;
			}

			const stepsWithDynamicTitles = applyDynamicTitlesToSteps(permittedSteps);

			setActiveTutorialId(tutorialName);
			setActiveTutorialName(tutorialName);
			activeTutorialNameRef.current = tutorialName;
			isManuallyStartedRef.current = startedManually;
			await stopTour();

			const driverInstance = driver();
			driverRef.current = driverInstance;
			setSharedDriverConfig(driverInstance);

			currentStepConfigsRef.current = stepsWithDynamicTitles;
			activeStepsRef.current = stepsWithDynamicTitles.map(
				(stepConfig: StepConfig) => ({
					element:
						elementsRef.current[
							stepConfig.reference as TourElementKey
						] instanceof HTMLElement
							? (elementsRef.current[
									stepConfig.reference as TourElementKey
								] as HTMLElement)
							: document.body,
					popover: stepConfig.popover,
				}),
			);

			if (stepsWithDynamicTitles.length > 0) {
				try {
					await ensureRouteAndElement(stepsWithDynamicTitles[0]);
				} catch (error) {
					console.error('[Tour] Failed to prepare first step', error);
				}
			}

			driverInstance.setSteps(activeStepsRef.current);
			driverInstance.drive();
			setIsTourActive(true);
		},
		[
			ensureRouteAndElement,
			setSharedDriverConfig,
			stopTour,
			tutorials,
			isLoadingTutorials,
			hasPermission,
			filterStepsByPermission,
			applyDynamicTitlesToSteps,
			matchesPort,
		],
	);

	const getAvailableTutorials = useCallback(() => {
		// Expose tutorial metadata for UI components (e.g., dropdowns or modals)
		if (!tutorials || tutorials.length === 0) {
			return [];
		}

		return tutorials.reduce<
			Array<{ id: string; name: string; enabled?: boolean }>
		>((available, tutorial) => {
			if (!hasPermission(tutorial.permissions)) {
				return available;
			}

			// Check if tutorial matches user's port
			const tutorialPort = tutorial.portsAvailable as string | undefined;
			if (!matchesPort(tutorialPort)) {
				return available;
			}

			const steps = mapTutorialToStepConfigs(tutorial);
			const permittedSteps = filterStepsByPermission(steps);
			if (permittedSteps.length === 0) {
				return available;
			}

			available.push({
				id: tutorial._id,
				name: tutorial.name,
				enabled: tutorial.enabled,
			});

			return available;
		}, []);
	}, [tutorials, hasPermission, filterStepsByPermission, matchesPort, userPort]);

	useEffect(() => {
		// Keep the popover responsive and stop the tour when the provider unmounts
		const handleResize = () => {
			if (driverRef.current?.isActive()) {
				const state = driverRef.current.getState();
				if (state.popover) {
					const popoverElement = state.popover.wrapper;
					if (popoverElement) {
						const viewportWidth = window.innerWidth;
						const viewportHeight = window.innerHeight;
						const isShortViewport = viewportHeight <= 800; // e.g. 1366×768 @ 13"
						const isMobile = viewportWidth <= 480;
						const isSmallTablet = viewportWidth > 480 && viewportWidth <= 767;
						const isIpadMini = viewportWidth >= 768 && viewportWidth <= 833;
						const isIpadPro11 = viewportWidth >= 834 && viewportWidth <= 1023;
						const isIpadPro129 = viewportWidth >= 1024 && viewportWidth <= 1366;

						// Same rem/vw values as onPopoverRender for DPI-independent scaling
						if (isShortViewport && !isMobile) {
							popoverElement.style.width = 'min(26rem, 32vw)';
							popoverElement.style.maxWidth = 'min(26rem, 32vw)';
							popoverElement.style.height = 'auto';
							popoverElement.style.minHeight = 'min(12rem, 45vh)';
							popoverElement.style.maxHeight = 'calc(100vh - 2rem)';
							popoverElement.style.bottom = 'min(1rem, 2vh)';
							popoverElement.style.right = 'min(1rem, 2vw)';
							popoverElement.style.left = 'auto';
							popoverElement.style.padding = '0.75rem';
						} else if (isMobile) {
							popoverElement.style.width = 'calc(100vw - 1.5rem)';
							popoverElement.style.maxWidth = 'none';
							popoverElement.style.height = 'auto';
							popoverElement.style.minHeight = 'min(11.25rem, 30vh)';
							popoverElement.style.maxHeight = 'calc(100vh - 2.5rem)';
							popoverElement.style.bottom = 'min(0.75rem, 1.5vh)';
							popoverElement.style.right = 'min(0.75rem, 2vw)';
							popoverElement.style.left = 'min(0.75rem, 2vw)';
						} else if (isSmallTablet) {
							popoverElement.style.width = 'min(26.25rem, 55vw)';
							popoverElement.style.maxWidth = 'min(26.25rem, 55vw)';
							popoverElement.style.height = 'auto';
							popoverElement.style.minHeight = 'min(11.875rem, 32vh)';
							popoverElement.style.maxHeight = 'calc(100vh - 2.75rem)';
							popoverElement.style.bottom = 'min(0.875rem, 1.5vh)';
							popoverElement.style.right = 'min(1rem, 2vw)';
							popoverElement.style.left = 'auto';
						} else if (isIpadMini) {
							popoverElement.style.width = 'min(28.125rem, 45vw)';
							popoverElement.style.maxWidth = 'min(28.125rem, 45vw)';
							popoverElement.style.height = 'auto';
							popoverElement.style.minHeight = 'min(12.5rem, 35vh)';
							popoverElement.style.maxHeight = 'calc(100vh - 3rem)';
							popoverElement.style.bottom = 'min(1rem, 2vh)';
							popoverElement.style.right = 'min(1rem, 2vw)';
							popoverElement.style.left = 'auto';
						} else if (isIpadPro11) {
							popoverElement.style.width = 'min(30rem, 42vw)';
							popoverElement.style.maxWidth = 'min(30rem, 42vw)';
							popoverElement.style.height = 'auto';
							popoverElement.style.minHeight = 'min(13.125rem, 36vh)';
							popoverElement.style.maxHeight = 'calc(100vh - 2.75rem)';
							popoverElement.style.bottom = 'min(1.125rem, 2vh)';
							popoverElement.style.right = 'min(1.25rem, 2vw)';
							popoverElement.style.left = 'auto';
						} else if (isIpadPro129) {
							popoverElement.style.width = 'min(32.5rem, 40vw)';
							popoverElement.style.maxWidth = 'min(32.5rem, 40vw)';
							popoverElement.style.height = 'auto';
							popoverElement.style.minHeight = 'min(13.75rem, 38vh)';
							popoverElement.style.maxHeight = 'calc(100vh - 3rem)';
							popoverElement.style.bottom = 'min(1.25rem, 2.5vh)';
							popoverElement.style.right = 'min(1.5rem, 2vw)';
							popoverElement.style.left = 'auto';
						} else {
							popoverElement.style.width = 'min(31.25rem, 38vw)';
							popoverElement.style.maxWidth = 'min(31.25rem, 38vw)';
							popoverElement.style.height = 'auto';
							popoverElement.style.minHeight = 'min(15.125rem, 35vh)';
							popoverElement.style.maxHeight = 'calc(100vh - 3rem)';
							popoverElement.style.bottom = 'min(1.5rem, 3vh)';
							popoverElement.style.right = 'min(1.5rem, 3vw)';
							popoverElement.style.left = 'auto';
						}
					}
				}
			}
		};

		window.addEventListener('resize', handleResize);

		return () => {
			window.removeEventListener('resize', handleResize);
			void stopTour();
		};
	}, [stopTour]);

	useEffect(() => {
		// Optionally kick off the onboarding user tutorial once on first mount
		if (
			!autoStart ||
			isTourActive ||
			isLoadingTutorials ||
			!tutorials ||
			tutorials.length === 0
		) {
			return;
		}

		const onboardingTutorialNames = ['OnboardingUser', 'OnboardingWCTutorial', 'OnboardingSalesRepTutorial', 'OnboardingTutorialGraftLogs', 'OnboardingTutorialPaymentMSL', 'OnboardingMindsightTutorial'];
		const onboardingTutorial = tutorials.find(
			(t) =>
				onboardingTutorialNames.includes(t.name) &&
				hasPermission(t.permissions) &&
				t.enabled !== false &&
				matchesPort(t.portsAvailable as string | undefined),
		);
		if (!onboardingTutorial) {
			console.warn(
				'[Tour] Onboarding tutorial not found, user lacks permission, or tutorial is disabled. Available tutorials:',
				tutorials.map((t) => t.name),
			);
			return;
		}

		void startTour(onboardingTutorial.name);
	}, [
		autoStart,
		isTourActive,
		startTour,
		tutorials,
		isLoadingTutorials,
		hasPermission,
		matchesPort,
	]);

	const value = useMemo(
		() => ({
			registerElement,
			startTour,
			stopTour,
			isTourActive,
			getAvailableTutorials,
			activeTutorialId,
			activeTutorialName,
		}),
		[
			activeTutorialId,
			activeTutorialName,
			getAvailableTutorials,
			isTourActive,
			registerElement,
			startTour,
			stopTour,
		],
	);

	return <TourContext.Provider value={value}>{children}</TourContext.Provider>;
};

// eslint-disable-next-line react-refresh/only-export-components
export const useTourContext = () => {
	const context = useContext(TourContext);
	if (!context) {
		throw new Error('useTourContext must be used within TourProvider');
	}
	return context;
};
